import { useEffect, useMemo, useState } from 'react'
import {
  CalendarDays,
  CheckCircle2,
  Loader2,
  Users,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

import { LocalWeatherWidget } from '@/components/layout/LocalWeatherWidget'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import { fetchCommandDashboard } from '@/lib/api/command'
import { fetchOpenCashShift } from '@/lib/api/accounts'
import { appNavLabel, getVisibleAppNavigation } from '@/lib/appNavigation'
import { getNavIcon } from '@/lib/navIcons'
import { cn } from '@/lib/utils'

/**
 * @param {number} hour
 * @param {(key: string, params?: Record<string, string | number>) => string} t
 */
function greetingForHour(hour, t) {
  if (hour < 12) return t('today.greetingMorning')
  if (hour < 17) return t('today.greetingAfternoon')
  return t('today.greetingEvening')
}

/**
 * @param {unknown} raw
 */
function normalizeTimeline(raw) {
  const appointments = Array.isArray(raw?.appointments)
    ? raw.appointments
    : Array.isArray(raw)
      ? raw
      : []

  return appointments.map((item) => ({
    id: item.id,
    time: item.time ?? item.time_label ?? '—',
    guest: item.guest ?? item.customer_name ?? item.guest_name ?? 'Walk-in',
    service: item.service ?? item.service_name ?? '',
    therapist: item.therapist ?? item.stylist_name ?? '',
    status: item.status,
  }))
}

function DashStatTile({ icon: Icon, label, value, hint, tone = 'default' }) {
  return (
    <div
      className={cn(
        'salon-dash-stat',
        tone === 'rose' && 'salon-dash-stat--rose',
        tone === 'sage' && 'salon-dash-stat--sage',
        tone === 'gold' && 'salon-dash-stat--gold',
      )}
    >
      <div className="salon-dash-stat-icon">
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-foreground">{value}</p>
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </div>
    </div>
  )
}

/**
 * @param {object} props
 * @param {string} props.to
 * @param {import('lucide-react').LucideIcon} props.icon
 * @param {string} props.label
 * @param {string} props.hint
 */
function QuickAccessTile({ to, icon: Icon, label, hint }) {
  return (
    <Link to={to} className="salon-dash-quick-tile group">
      <div className="salon-dash-quick-tile-icon">
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-foreground group-hover:text-primary">{label}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{hint}</p>
      </div>
    </Link>
  )
}

/**
 * Compact hero chip used between weather and primary actions.
 * @param {{ to: string, icon: import('lucide-react').LucideIcon, label: string, tone?: 'default' | 'accent' }} props
 */
function HeroQuickChip({ to, icon: Icon, label, tone = 'default' }) {
  const isAccent = tone === 'accent'
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-2xl px-3',
        'text-[13px] font-semibold shadow-sm transition-all',
        'hover:-translate-y-0.5 hover:shadow-md',
        isAccent
          ? 'border border-[#8A6D42]/30 bg-[#8A6D42] text-white hover:bg-[#7A6039]'
          : 'border border-[#E5DED3]/90 bg-white/85 text-foreground backdrop-blur-sm hover:border-primary/30 hover:bg-white hover:text-primary',
      )}
    >
      <span
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-lg',
          isAccent ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary',
        )}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
      </span>
      <span className="whitespace-nowrap">{label}</span>
    </Link>
  )
}

const HERO_QUICK_IDS = ['calendar', 'checkout', 'customers', 'online-bookings', 'pos']

/** Hero-only action labels — keep menu labels unchanged. */
const HERO_QUICK_LABEL_KEYS = {
  calendar: 'today.heroBookVisit',
  checkout: 'today.heroTakePayment',
  customers: 'today.heroFindGuest',
  'online-bookings': 'today.heroWebRequests',
  pos: 'today.heroPos',
}

/** Extra hero actions not taken from nav entries (same route as checkout). */
const HERO_QUICK_EXTRA = {
  pos: { id: 'pos', path: '/pos', icon: 'receipt' },
}

export function TodayPage() {
  const { user } = useAuth()
  const { activeBranchId, activeBranch, canSwitchBranch } = useBranch()
  const { t, locale } = useUiLocale()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  /** @type {[Record<string, unknown> | null, Function]} */
  const [data, setData] = useState(null)
  const [dayOpen, setDayOpen] = useState(/** @type {boolean | null} */ (null))

  useEffect(() => {
    if (!activeBranchId) {
      return undefined
    }

    let cancelled = false

    async function load() {
      setLoading(true)
      setError('')
      setData(null)
      try {
        const [payload, shift] = await Promise.all([
          fetchCommandDashboard(),
          fetchOpenCashShift().catch(() => null),
        ])
        if (!cancelled) {
          setData(payload)
          setDayOpen(Boolean(shift))
        }
      } catch {
        if (!cancelled) {
          setError(t('today.error'))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [t, activeBranchId])

  const firstName = user?.name?.split(' ')[0] ?? 'there'
  const greeting = greetingForHour(new Date().getHours(), t)
  const dateLabel = new Intl.DateTimeFormat(locale === 'ar' ? 'ar-AE' : 'en-AE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date())

  const kpis = Array.isArray(data?.kpis) ? data.kpis : []
  const timeline = useMemo(() => normalizeTimeline(data?.timeline), [data?.timeline])
  const snapshot = data?.snapshot ?? {}

  const apptKpi = kpis.find((k) => k.title === 'Appointments')
  const appointmentCount = Number(apptKpi?.value ?? timeline.length ?? 0)

  const completedCount = useMemo(() => {
    return timeline.filter((item) => item.status === 'completed' || item.status === 'done').length
  }, [timeline])

  const guests = snapshot?.guests ?? {}

  const quickLinks = useMemo(() => {
    const hints = {
      calendar: t('today.quickAppointments'),
      'online-bookings': t('today.quickOnline'),
      checkout: t('today.quickPos'),
      customers: t('today.quickCustomers'),
      services: t('today.quickServices'),
      staff: t('today.quickStaff'),
      sales: t('today.quickSales'),
      'send-offers': t('today.quickOffers'),
      settings: t('today.quickSettings'),
    }

    return getVisibleAppNavigation(user)
      .filter((entry) => entry.id !== 'today')
      .map((entry) => ({
        id: entry.id,
        to: entry.path,
        icon: getNavIcon(entry.icon),
        label: appNavLabel(entry, t),
        hint: hints[entry.id] ?? '',
      }))
  }, [user, t])

  const heroQuickLinks = useMemo(
    () =>
      HERO_QUICK_IDS.map((id) => {
        const extra = HERO_QUICK_EXTRA[id]
        const link = extra
          ? {
              id: extra.id,
              to: extra.path,
              icon: getNavIcon(extra.icon),
              label: '',
            }
          : quickLinks.find((item) => item.id === id)
        if (!link) return null
        const labelKey = HERO_QUICK_LABEL_KEYS[id]
        return {
          ...link,
          label: labelKey ? t(labelKey) : link.label,
          tone: id === 'pos' ? 'accent' : 'default',
        }
      }).filter(Boolean),
    [quickLinks, t],
  )

  const moreQuickLinks = useMemo(
    () =>
      quickLinks.filter(
        (link) => !HERO_QUICK_IDS.includes(link.id) && link.id !== 'checkout',
      ),
    [quickLinks],
  )

  return (
    <div className="salon-page">
      {error ? (
        <p className="mb-6 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
          {error}
        </p>
      ) : null}

      {dayOpen === false ? (
        <p className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Day is closed.{' '}
          <Link className="font-semibold underline" to="/cash">
            Open the day
          </Link>{' '}
          before taking payment.
        </p>
      ) : null}
      {dayOpen === true ? (
        <p className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
          Day is open · Ready for checkout
        </p>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-24 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          <span>{t('today.loading')}</span>
        </div>
      ) : (
        <div className="space-y-6">
          <motion.section
            className="salon-dash-hero"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex flex-wrap items-center gap-3 lg:gap-4">
              <div className="min-w-0 shrink-0">
                <p className="text-xs font-medium text-primary/90 sm:text-sm">{dateLabel}</p>
                <h1 className="mt-0.5 text-lg font-bold tracking-tight text-foreground sm:text-xl">
                  {greeting}, {firstName}
                </h1>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">
                  {canSwitchBranch && activeBranch?.name
                    ? t('today.subtitleBranch', { branch: activeBranch.name })
                    : t('today.subtitle')}
                </p>
              </div>
              <LocalWeatherWidget variant="dashboard" className="shrink-0" />
              {heroQuickLinks.length > 0 ? (
                <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {heroQuickLinks.map((link) => (
                    <HeroQuickChip
                      key={link.id}
                      to={link.to}
                      icon={link.icon}
                      label={link.label}
                      tone={link.tone}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </motion.section>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <DashStatTile
              icon={CalendarDays}
              label={t('today.appointmentsToday')}
              value={String(appointmentCount)}
              hint={t('today.scheduledToday')}
              tone="rose"
            />
            <DashStatTile
              icon={CheckCircle2}
              label={t('today.completed')}
              value={String(completedCount)}
              hint={t('today.servicesDone')}
              tone="sage"
            />
            <DashStatTile
              icon={Users}
              label={t('today.guestsToday')}
              value={String((guests.new ?? 0) + (guests.returning ?? 0))}
              hint={t('today.newReturning', {
                new: guests.new ?? 0,
                returning: guests.returning ?? 0,
              })}
            />
          </div>

          {moreQuickLinks.length > 0 ? (
            <section className="salon-panel">
              <h2 className="text-lg font-semibold tracking-tight">{t('today.quickAccess')}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t('today.quickAccessHint')}</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {moreQuickLinks.map((link) => (
                  <QuickAccessTile
                    key={link.id}
                    to={link.to}
                    icon={link.icon}
                    label={link.label}
                    hint={link.hint}
                  />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}
    </div>
  )
}
